/**
 * Complete Your Stack
 *
 * A one-click product bundle widget for the PDP. The current product
 * renders as a locked card; additional cards come from merchant-picked
 * "manual" blocks (rendered server-side by bundle-stack-card.liquid) or
 * from Shopify's recommendations, which THIS FILE fetches from the lean
 * recommendations JSON endpoint and renders client-side.
 *
 * Why not the Section Rendering API for recommendations? Re-rendering the
 * whole parent section just to source a few small cards can be
 * 100-200KB+ of HTML for 2-4 products, and this widget sits right below
 * the buy box - customers notice that as "slow". The JSON endpoint is a
 * few KB of real product data, fetched eagerly (not gated behind an
 * IntersectionObserver) so it's normally resolved before anyone scrolls
 * to it.
 *
 * Every card (locked, manual, recommendation) shares one data contract: a
 * `<script type="application/json" data-stack-data>` blob shaped like
 *   {
 *     productId, title, url,
 *     fallbackImage: { src } | null,
 *     optionNames: [String],
 *     variants: [{ id, title, price, compare_at_price, available,
 *                   featured_image: { src } | null,
 *                   option1, option2, option3 }]
 *   }
 * and a `data-current-variant-id` attribute on the card root that this
 * file treats as the single source of truth for "which variant of this
 * card's product is currently selected". Recalculation, variant-change
 * handling, and add-to-cart all read from that contract, so it doesn't
 * matter whether a card came from Liquid or was built here in JS.
 */
(function () {
  if (customElements.get('complete-your-stack')) return;

  const ICON_CHECK_SVG =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-check" width="16" height="12" viewBox="0 0 16 12" fill="none">' +
    '<path d="M14.6666 0.87146L6.57135 11.1286L1.33325 5.91908" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Matches snippets/icon-caret-down.liquid - kept identical so JS-built
  // recommendation cards get the same dropdown arrow as Liquid-rendered ones.
  const ICON_CARET_DOWN_SVG =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret-down" viewBox="0 0 24 24" fill="none">' +
    '<path d="M22 7L12 17L2 7" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>' +
    '</svg>';

  const RECOMMENDATIONS_FETCH_TIMEOUT = 8000;

  /**
   * Escapes a string for safe interpolation into an HTML template literal.
   * Every piece of product/variant JSON we render via innerHTML (titles,
   * variant titles, image URLs) goes through this first.
   */
  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        default:
          return '&#39;';
      }
    });
  }

  /**
   * Handles the image-shape asymmetry between Liquid-rendered card data
   * (always `{ src }`) and Shopify's recommendations JSON endpoint (variant
   * images are `{ src }`, but product-level images are plain URL strings).
   */
  function imageSrc(image) {
    if (!image) return null;
    if (typeof image === 'string') return image;
    return image.src || null;
  }

  class CompleteYourStack extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.cartType = this.dataset.cartType;
      this.showPercentOff = this.dataset.showPercentOff === 'true';

      this.cardListEl = this.querySelector('[data-stack-card-list]');
      this.addButton = this.querySelector('[data-stack-add-button]');
      this.addButtonText = this.querySelector('[data-stack-add-button-text]');
      this.statusMessageEl = this.querySelector('[data-stack-status-message]');
      this.countEl = this.querySelector('[data-stack-count]');
      this.subtotalEl = this.querySelector('[data-stack-subtotal]');
      this.savingsRowEl = this.querySelector('[data-stack-savings-row]');
      this.savingsEl = this.querySelector('[data-stack-savings]');
      this.totalEl = this.querySelector('[data-stack-total]');

      const currencyScript = this.querySelector('script[data-stack-currency]');
      this.currencyData = currencyScript ? JSON.parse(currencyScript.textContent) : null;

      this._availabilityBlocked = false;
      this._mainVariantPickers = [];
      this._isAdding = false;

      this.bindEvents();
      this.observeCardList();
      this.connectMainVariantPickerSync();
      this.loadRecommendationsIfNeeded();
      this.recalculate();
    }

    // ---------------------------------------------------------------------
    // Card data helpers - the shared contract every card (Liquid or JS
    // built) is read through.
    // ---------------------------------------------------------------------

    getCards() {
      return Array.from(this.querySelectorAll('[data-stack-card]'));
    }

    getCardData(card) {
      if (card._stackData) return card._stackData;
      const script = card.querySelector('script[data-stack-data]');
      let data = { variants: [], optionNames: [] };
      if (script) {
        try {
          data = JSON.parse(script.textContent);
        } catch (e) {
          console.error('[complete-your-stack] invalid card data', e);
        }
      }
      card._stackData = data;
      return data;
    }

    getCardVariant(card) {
      const data = this.getCardData(card);
      const variants = data.variants || [];
      const currentId = card.dataset.currentVariantId;
      return variants.find((variant) => String(variant.id) === String(currentId)) || variants[0] || null;
    }

    // ---------------------------------------------------------------------
    // Money formatting - same approach as the theme's <pdp-price> component.
    // ---------------------------------------------------------------------

    formatMoney(cents) {
      if (!this.currencyData) return cents;
      const { currency_code_enabled, money_format, money_with_currency_format, locale_code } = this.currencyData;
      const options = { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 };
      const formatted = new Intl.NumberFormat(locale_code || window.Shopify?.locale, options).format(
        Number(cents) / 100
      );
      const shopifyFormat = currency_code_enabled ? money_with_currency_format : money_format;
      return (shopifyFormat || '${{amount}}').replace('{{amount}}', formatted);
    }

    // ---------------------------------------------------------------------
    // Events
    // ---------------------------------------------------------------------

    bindEvents() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-stack-checkbox]')) {
          this.recalculate();
        } else if (event.target.matches('[data-stack-variant-select]')) {
          this.onCardVariantChange(event);
        }
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addBundleToCart());
      }
    }

    /**
     * Recommendation cards load in asynchronously after first paint, so a
     * one-time querySelectorAll at construction time isn't enough - watch
     * the list for mutations and recompute whenever it changes.
     */
    observeCardList() {
      if (!this.cardListEl) return;
      const observer = new MutationObserver(() => this.recalculate());
      observer.observe(this.cardListEl, { childList: true, subtree: true });
    }

    // ---------------------------------------------------------------------
    // Variant change (per-card <select>)
    // ---------------------------------------------------------------------

    onCardVariantChange(event) {
      const select = event.target;
      const card = select.closest('[data-stack-card]');
      if (!card) return;
      const data = this.getCardData(card);
      const variant = (data.variants || []).find((v) => String(v.id) === select.value);
      if (!variant) return;

      card.dataset.currentVariantId = String(variant.id);
      this.updateCardVisuals(card, data, variant);
      this.recalculate();

      // The locked card's own <select> is a second, independent picker for
      // the same product as the PDP's main variant picker - push the
      // change there too so the two stay in sync in both directions.
      if (card.dataset.locked === 'true') {
        this.pushVariantToMainPicker(data, variant);
      }
    }

    updateCardVisuals(card, data, variant) {
      const priceSaleEl = card.querySelector('[data-stack-price-sale]');
      const priceCompareEl = card.querySelector('[data-stack-price-compare]');
      const percentOffEl = card.querySelector('[data-stack-percent-off]');
      const imageEl = card.querySelector('[data-stack-image]');
      const statusEl = card.querySelector('[data-stack-card-status]');

      const price = variant.price;
      const compareAt = variant.compare_at_price;
      const onSale = !!(compareAt && compareAt > price);

      if (priceSaleEl) priceSaleEl.textContent = this.formatMoney(price);
      if (priceCompareEl) {
        priceCompareEl.textContent = onSale ? this.formatMoney(compareAt) : '';
        priceCompareEl.hidden = !onSale;
      }
      if (percentOffEl) {
        if (onSale && this.showPercentOff) {
          const pct = Math.round(((compareAt - price) / compareAt) * 100);
          percentOffEl.textContent = `-${pct}%`;
          percentOffEl.hidden = false;
        } else {
          percentOffEl.hidden = true;
        }
      }

      if (imageEl) {
        const src = imageSrc(variant.featured_image) || imageSrc(data.fallbackImage);
        if (src) imageEl.src = src;
      }

      if (statusEl) {
        const soldOut = !variant.available;
        statusEl.textContent = soldOut ? window.variantStrings?.soldOut || 'Sold out' : '';
        statusEl.hidden = !soldOut;
      }

      card.classList.toggle('stack-card-unavailable', !variant.available);
    }

    // ---------------------------------------------------------------------
    // Sync with the PDP's own main variant picker
    //
    // <variant-radios> dispatches a non-bubbling `variantChange` CustomEvent
    // on itself (see assets/global.js), so a listener on `document` would
    // never see it - it has to be attached to the picker element directly.
    // To stay safe when this widget is used both nested inside another
    // section and as its own standalone section, we search the whole
    // document for variant pickers rather than scoping to an ancestor, and
    // guard by the product's URL (the one identifying attribute the
    // picker's markup exposes) so we never react to a picker for an
    // unrelated product (e.g. inside a quick-add modal elsewhere).
    // ---------------------------------------------------------------------

    connectMainVariantPickerSync() {
      const lockedCard = this.querySelector('[data-stack-card][data-locked="true"]');
      if (!lockedCard) return;
      const data = this.getCardData(lockedCard);
      if (!data.url) return;

      const pickers = document.querySelectorAll('variant-radios, variant-selects');
      pickers.forEach((picker) => {
        if (picker.dataset.url !== data.url) return;
        this._mainVariantPickers.push(picker);
        picker.addEventListener('variantChange', (event) => {
          this.onMainPickerVariantChange(lockedCard, data, event.detail);
        });
      });
    }

    onMainPickerVariantChange(lockedCard, data, variantDetail) {
      if (!variantDetail || variantDetail.id == null) return;
      const variant =
        (data.variants || []).find((v) => String(v.id) === String(variantDetail.id)) || variantDetail;

      lockedCard.dataset.currentVariantId = String(variantDetail.id);
      const select = lockedCard.querySelector('[data-stack-variant-select]');
      if (select) select.value = String(variantDetail.id);

      this.updateCardVisuals(lockedCard, data, variant);
      this.recalculate();
    }

    /**
     * Pushes a variant chosen via the locked card's own <select> back onto
     * the PDP's main variant picker, so the two pickers for the same
     * product never disagree.
     */
    pushVariantToMainPicker(data, variant) {
      if (!this._mainVariantPickers.length) return;
      const optionNames = data.optionNames || [];
      const optionValues = [variant.option1, variant.option2, variant.option3];

      this._mainVariantPickers.forEach((picker) => {
        let lastInput = null;
        optionNames.forEach((name, index) => {
          const value = optionValues[index];
          if (value == null) return;
          const input = picker.querySelector(
            `input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
          );
          if (input) {
            input.checked = true;
            lastInput = input;
          }
        });
        if (lastInput) {
          lastInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // ---------------------------------------------------------------------
    // Recalculation
    // ---------------------------------------------------------------------

    recalculate() {
      const cards = this.getCards();
      let count = 0;
      let subtotal = 0;
      let total = 0;
      let hasUnavailable = false;

      cards.forEach((card) => {
        const checkbox = card.querySelector('[data-stack-checkbox]');
        const checked = checkbox ? checkbox.checked : false;
        const variant = this.getCardVariant(card);
        if (!variant) return;

        if (checked) {
          count += 1;
          const compareAt = variant.compare_at_price && variant.compare_at_price > variant.price
            ? variant.compare_at_price
            : variant.price;
          subtotal += compareAt;
          total += variant.price;
          if (!variant.available) hasUnavailable = true;
        }
      });

      const savings = subtotal - total;

      if (this.countEl) {
        const key = count === 1 ? 'one' : 'other';
        this.countEl.textContent =
          window.bundleStrings?.itemsCount?.[key]?.replace('[count]', count) || `${count} item${count === 1 ? '' : 's'} selected`;
      }
      if (this.subtotalEl) {
        this.subtotalEl.textContent = this.formatMoney(subtotal);
        this.subtotalEl.hidden = savings <= 0;
      }
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);
      if (this.savingsEl) this.savingsEl.textContent = this.formatMoney(savings);
      if (this.savingsRowEl) this.savingsRowEl.hidden = savings <= 0;

      if (this.addButton) this.addButton.disabled = hasUnavailable || count === 0;

      // The "Adding..." label owns the button text while a request is in
      // flight - don't fight it. Otherwise this is the single source of
      // truth for the idle-state label, so the count shown always matches
      // what's actually checked.
      if (this.addButtonText && !this._isAdding) {
        if (count > 0) {
          const key = count === 1 ? 'one' : 'other';
          this.addButtonText.textContent =
            window.bundleStrings?.addToCartCount?.[key]?.replace('[count]', count) || `Add Bundle (${count})`;
        } else {
          this.addButtonText.textContent = window.bundleStrings?.addToCart || 'Add Bundle to Cart';
        }
      }

      // Never let "Add Bundle" attempt to add a sold-out variant: surface a
      // clear, edge-triggered status message rather than letting the cart
      // API reject the request. Edge-triggered so it doesn't stomp on a
      // transient success/error message left by a previous add attempt.
      if (hasUnavailable) {
        this.setStatusMessage(
          window.bundleStrings?.unavailableSelection ||
            'One or more selected items are currently unavailable. Please choose a different option.',
          true
        );
        this._availabilityBlocked = true;
      } else if (this._availabilityBlocked) {
        this.setStatusMessage('');
        this._availabilityBlocked = false;
      }
    }

    setStatusMessage(message, isError = false) {
      if (!this.statusMessageEl) return;
      this.statusMessageEl.textContent = message;
      this.statusMessageEl.hidden = !message;
      this.statusMessageEl.classList.toggle('stack-status-error', !!isError);
    }

    // ---------------------------------------------------------------------
    // Recommendations
    // ---------------------------------------------------------------------

    loadRecommendationsIfNeeded() {
      const container = this.querySelector('[data-stack-recommendations]');
      if (!container) return;
      const url = container.dataset.url;
      if (!url) return;

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeout = controller
        ? setTimeout(() => controller.abort(), RECOMMENDATIONS_FETCH_TIMEOUT)
        : null;

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then((response) => {
          if (!response.ok) throw new Error(`Recommendations request failed with ${response.status}`);
          return response.json();
        })
        .then((json) => this.renderRecommendations(json, container))
        .catch((error) => {
          console.error('[complete-your-stack] failed to load recommended products', error);
        })
        .finally(() => {
          if (timeout) clearTimeout(timeout);
          container.remove();
        });
    }

    renderRecommendations(json, container) {
      const products = Array.isArray(json.products) ? json.products : [];
      const limit = parseInt(container.dataset.limit, 10) || 3;
      const excludeIds = (container.dataset.excludeIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      const currentProductId = this.dataset.productId;

      const filtered = products
        .filter((product) => String(product.id) !== String(currentProductId))
        .filter((product) => !excludeIds.includes(String(product.id)))
        .filter((product) => (product.variants || []).some((variant) => variant.available))
        .slice(0, limit);

      if (!this.cardListEl) return;
      filtered.forEach((product, index) => {
        this.cardListEl.appendChild(this.buildRecommendationCard(product, index));
      });
    }

    buildRecommendationCard(product, index) {
      const cardId = `rec-${index}-${product.id}`;
      const variants = (product.variants || []).map((variant) => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        available: variant.available,
        featured_image: variant.featured_image || null,
        option1: variant.option1 != null ? variant.option1 : null,
        option2: variant.option2 != null ? variant.option2 : null,
        option3: variant.option3 != null ? variant.option3 : null,
      }));

      const startingVariant = variants.find((v) => v.available) || variants[0] || {
        id: null,
        price: 0,
        compare_at_price: null,
        available: false,
      };

      const fallbackImageSrc =
        imageSrc(product.featured_image) || imageSrc((product.images || [])[0]) || null;

      const data = {
        productId: product.id,
        title: product.title,
        url: product.url,
        fallbackImage: fallbackImageSrc ? { src: fallbackImageSrc } : null,
        optionNames: (product.options || []).map((option) => (typeof option === 'string' ? option : option.name)),
        variants,
      };

      const imgSrc = imageSrc(startingVariant.featured_image) || fallbackImageSrc || '';
      const onSale = !!(
        startingVariant.compare_at_price && startingVariant.compare_at_price > startingVariant.price
      );
      const pct = onSale
        ? Math.round(((startingVariant.compare_at_price - startingVariant.price) / startingVariant.compare_at_price) * 100)
        : 0;
      const checkboxId = `StackCard-${this.sectionId}-${cardId}`;
      const titleId = `StackCardTitle-${this.sectionId}-${cardId}`;

      const li = document.createElement('li');
      li.className = 'stack-card';
      li.setAttribute('data-stack-card', '');
      li.dataset.locked = 'false';
      li.dataset.currentVariantId = startingVariant.id != null ? String(startingVariant.id) : '';

      li.innerHTML = `
        <div class="stack-card-media">
          <img data-stack-image class="stack-card-image" src="${escapeHtml(imgSrc)}" width="200" height="200" loading="lazy" alt="${escapeHtml(product.title)}">
        </div>
        <div class="stack-card-info">
          <div class="stack-card-heading-row">
            <p class="stack-card-title" id="${escapeHtml(titleId)}"><a href="${escapeHtml(product.url)}" class="unstyled-link">${escapeHtml(product.title)}</a></p>
          </div>
          ${variants.length > 1 ? this.buildVariantSelectMarkup(variants, startingVariant.id, titleId) : ''}
          <div class="stack-card-price" data-stack-price>
            <span class="stack-card-price-compare" data-stack-price-compare${onSale ? '' : ' hidden'}>${onSale ? escapeHtml(this.formatMoney(startingVariant.compare_at_price)) : ''}</span>
            <span class="stack-card-price-sale" data-stack-price-sale>${escapeHtml(this.formatMoney(startingVariant.price))}</span>
            ${this.showPercentOff ? `<span class="stack-card-percent-off" data-stack-percent-off${onSale ? '' : ' hidden'}>-${pct}%</span>` : ''}
          </div>
          <p class="stack-card-status" data-stack-card-status hidden></p>
        </div>
        <div class="stack-card-select">
          <div class="stack-card-checkbox">
            <input type="checkbox" id="${escapeHtml(checkboxId)}" data-stack-checkbox checked>
            <label for="${escapeHtml(checkboxId)}">
              ${ICON_CHECK_SVG}
              <span class="visually-hidden">Add ${escapeHtml(product.title)} to bundle</span>
            </label>
          </div>
        </div>
      `;

      const script = document.createElement('script');
      script.type = 'application/json';
      script.setAttribute('data-stack-data', '');
      script.textContent = JSON.stringify(data);
      li.appendChild(script);

      if (!startingVariant.available) {
        const statusEl = li.querySelector('[data-stack-card-status]');
        if (statusEl) {
          statusEl.textContent = window.variantStrings?.soldOut || 'Sold out';
          statusEl.hidden = false;
        }
        li.classList.add('stack-card-unavailable');
      }

      return li;
    }

    buildVariantSelectMarkup(variants, selectedId, titleId) {
      const options = variants
        .map((variant) => {
          const disabled = !variant.available;
          const label = escapeHtml(variant.title) + (disabled ? ` — ${window.variantStrings?.soldOut || 'Sold out'}` : '');
          return `<option value="${variant.id}" ${String(variant.id) === String(selectedId) ? 'selected' : ''} ${disabled ? 'disabled' : ''}>${label}</option>`;
        })
        .join('');
      return `<div class="select field-with-icon stack-card-variant-select-wrapper"><select class="stack-card-variant-select field-select" data-stack-variant-select aria-labelledby="${escapeHtml(titleId)}">${options}</select>${ICON_CARET_DOWN_SVG}</div>`;
    }

    // ---------------------------------------------------------------------
    // Add to cart
    // ---------------------------------------------------------------------

    getCheckedItems() {
      return this.getCards()
        .map((card) => ({ card, checkbox: card.querySelector('[data-stack-checkbox]'), variant: this.getCardVariant(card) }))
        .filter(({ checkbox, variant }) => checkbox && checkbox.checked && variant && variant.available)
        .map(({ variant }) => variant.id);
    }

    getSectionsToRender() {
      const sections = ['cart-icon-bubble'];
      if (this.cartType === 'drawer') sections.push('cart-drawer');
      return sections;
    }

    async addBundleToCart() {
      if (!this.addButton || this.addButton.disabled) return;
      const variantIds = this.getCheckedItems();
      if (!variantIds.length) return;

      this.addButton.disabled = true;
      this.addButton.classList.add('loading');
      this._isAdding = true;
      const spinner = this.addButton.querySelector('.loading-overlay-spinner');
      if (spinner) spinner.classList.remove('hidden');
      if (this.addButtonText) {
        this.addButtonText.textContent = window.bundleStrings?.adding || 'Adding...';
      }

      const body = JSON.stringify({
        items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
        sections: this.getSectionsToRender(),
      });

      try {
        const response = await fetch(window.routes.cart_add_url, { ...fetchConfig('javascript'), body });
        const result = await response.json();

        if (result.status || result.errors) {
          const message =
            result.description ||
            result.message ||
            (typeof result.errors === 'string' ? result.errors : null) ||
            window.bundleStrings?.genericError ||
            'Something went wrong adding the bundle to your cart. Please try again.';
          this.setStatusMessage(message, true);
          return;
        }

        this.handleSuccessfulAdd(result);
      } catch (error) {
        console.error('[complete-your-stack] add to cart failed', error);
        this.setStatusMessage(
          window.bundleStrings?.genericError || 'Something went wrong adding the bundle to your cart. Please try again.',
          true
        );
      } finally {
        this.addButton.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        this._isAdding = false;
        this.recalculate();
      }
    }

    handleSuccessfulAdd(result) {
      const cartDrawer = this.cartType === 'drawer' ? document.querySelector('cart-drawer') : null;

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // cart-drawer.renderContents() is this theme's own native drawer
        // refresh (the exact same call product-form.js makes) - it
        // synchronously injects a fresh <cart-drawer-items>, whose
        // connectedCallback immediately (re)subscribes to the cart-update
        // pub/sub event. Publishing cart-update right after this, in the
        // same tick, would make that brand-new listener kick off a SECOND,
        // independent fetch + DOM replace on top of the render that's
        // already in flight, racing cart-drawer.js's own open()/trapFocus()
        // sequence and corrupting the drawer (blank, unusable until
        // refresh). product-form.js never publishes cart-update after its
        // own renderContents() call for the same reason - don't either.
        if (typeof cartDrawer.setActiveElement === 'function') {
          cartDrawer.setActiveElement(this.addButton);
        }

        // Work around a separate bug in cart-drawer.js: when the cart
        // starts empty, <cart-drawer> gets an "is-empty" class on page
        // load (snippets/cart-drawer.liquid). renderContents() only ever
        // tries to clear "is-empty" from .drawer-inner, which never
        // actually carries that class in this theme's markup - so it
        // silently never clears on <cart-drawer> itself. open() then still
        // thinks the drawer is empty and looks for the now-gone
        // .drawer-inner-empty placeholder instead of #CartDrawer, gets
        // null, and trapFocus/getFocusableElements throws on it - drawer
        // gets stuck mid-open. This almost certainly affects the theme's
        // native "Add to Cart" too on a first, from-empty add, just less
        // often hit in testing than an upsell widget is. Clearing it here
        // rather than editing the shared cart-drawer.js, which would
        // change every add-to-cart flow site-wide.
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(result);
      } else {
        this.updateCartIconBubble(result);
        if (this.cartType === 'page') {
          window.location.href = window.routes.cart_url;
        } else {
          const count = Array.isArray(result.items) ? result.items.length : 1;
          const key = count === 1 ? 'one' : 'other';
          this.setStatusMessage(
            window.bundleStrings?.addedToCart?.[key]?.replace('[count]', count) ||
              `${count} item${count === 1 ? '' : 's'} added to your cart.`
          );
        }

        // No drawer render just happened, so there's no risk of the
        // cart-drawer-items race above - safe to broadcast for any other
        // component on the page (quantity rules, volume pricing, etc.)
        // that wants to react natively to the cart changing.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'complete-your-stack', cartData: result });
      }
    }

    updateCartIconBubble(result) {
      const html = result.sections && result.sections['cart-icon-bubble'];
      const target = document.getElementById('cart-icon-bubble');
      if (!html || !target) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (parsed) target.innerHTML = parsed.innerHTML;
    }
  }

  customElements.define('complete-your-stack', CompleteYourStack);
})();
